import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { formatMoneyDigits, paisa, parseRupeeInput, type Money } from '@fleet/shared';
import { useAppConfig } from '@/app/appConfig';
import { CountrySelect } from './CountrySelect';
import { COUNTRIES, HOME_COUNTRY, countryOf } from '@/lib/countries';

/**
 * Form controls.
 *
 * Built for typing speed: labels above inputs so the eye travels straight down,
 * 44px controls, and errors under the field they belong to rather than gathered
 * at the top where nobody reads them.
 *
 * Every control is a real label bound to a real input. No placeholder-as-label:
 * it disappears the moment someone starts typing, which is exactly when they
 * need it.
 */

interface FieldProps {
  readonly label: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

/**
 * A label and a control.
 *
 * There is deliberately no `hint`. Every field used to be allowed a sentence
 * of explanation underneath, and twenty-eight of them had one — which turned
 * a five-field form into a page of prose, in a second language, for staff who
 * are working with a customer in front of them. The client's instruction was
 * blunt: remove them, and make the label say it.
 *
 * So the constraint is here rather than in a style guide. A label that cannot
 * be understood on its own needs rewording, not a paragraph beneath it, and
 * there is now nowhere to put the paragraph.
 */
export function Field({ label, error, required, children }: FieldProps): ReactElement {
  const id = useId();
  const errorId = `${id}-error`;
  const describedBy = error !== undefined ? errorId : undefined;

  // `min-w-0`: a grid or flex child defaults to min-width:auto, so a control
  // wider than its share pushes the row out instead of shrinking. That is how
  // a single over-wide time box put a horizontal scrollbar on the whole page.
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-small font-medium">
        {label}
        {required === true && (
          <span className="ml-1 text-ink-muted" aria-hidden="true">
            (required)
          </span>
        )}
      </label>
      {children({ id, describedBy })}
      {error !== undefined && (
        <p id={errorId} className="text-small font-medium text-alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Everything a control looks like *except* how tall it is.
 *
 * Height is separate because it is the one thing that varies, and because
 * appending `h-8` to a string that already contains `min-h-touch` does not
 * override it — the two are the same utility group at the same specificity,
 * so which one wins is decided by the order Tailwind happens to emit them in,
 * not by the order they are written. That is a coin toss dressed up as code,
 * and it has now produced two visual bugs in this file: a time box that grew
 * to fill its row, and a select that stood twelve pixels taller than the
 * buttons beside it. Composing from a base that names no height cannot fail
 * that way.
 */
const CONTROL_BASE =
  'w-full rounded-sm border border-rule-strong bg-panel px-2.5 ' +
  'text-ink placeholder:text-ink-faint disabled:bg-paper disabled:text-ink-faint';

/** 44px. The default everywhere, and what a form typed at speed needs. */
const CONTROL = `${CONTROL_BASE} min-h-touch text-body`;

/** 32px, matching `Button`'s base size, for a control that sits among buttons. */
const CONTROL_COMPACT = `${CONTROL_BASE} h-8 text-base`;

interface TextInputProps {
  readonly id: string;
  readonly describedBy: string | undefined;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly type?: 'text' | 'tel' | 'password' | 'date' | 'datetime-local';
  readonly autoComplete?: string;
  readonly autoFocus?: boolean;
  /** Plates, licence numbers, references: monospace, because they are identifiers. */
  readonly identifier?: boolean;
}

export function TextInput({
  id,
  describedBy,
  value,
  onChange,
  type = 'text',
  autoComplete,
  autoFocus,
  identifier = false,
}: TextInputProps): ReactElement {
  return (
    <input
      id={id}
      type={type}
      value={value}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      aria-describedby={describedBy}
      onChange={(event) => onChange(event.target.value)}
      className={`${CONTROL} ${identifier ? 'identifier uppercase' : ''}`}
    />
  );
}

interface PhoneInputProps {
  readonly id: string;
  readonly describedBy: string | undefined;
  /** Stored as `+<country code><number>`, or empty. */
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * A country code and a number.
 *
 * Tourists book cars, and so do agents ringing from Delhi — and every one of
 * those numbers used to be refused for not looking Nepali. The counter then
 * either lost the booking or typed something false to get past the box, which
 * is worse: a made-up number looks reachable.
 *
 * Nepal is the default and sits at the top of the list, because it is the
 * answer nineteen times in twenty here. The value crossing the boundary is one
 * string — `+9779801234567` — so nothing above this has to think about the two
 * halves.
 */
export function PhoneInput({ id, describedBy, value, onChange }: PhoneInputProps): ReactElement {
  /**
   * The chosen country, held here rather than read back out of the number.
   *
   * It used to be `countryOf(value)` — derived entirely from the stored
   * string — and that could not work for two reasons.
   *
   * With an empty box there is nothing to derive *from*: picking India
   * produced no digits, so the value stayed empty, so the country read back
   * as the Nepal fallback and the choice vanished the instant it was made.
   * That is the bug as reported.
   *
   * And even with a number typed it could not round-trip, because a dial code
   * does not identify a country: the United States and Canada are both +1,
   * Russia and Kazakhstan are both +7. Choosing Canada stored `+1…` and read
   * back as the United States, for ever.
   *
   * So the selection is state. The value is still the single thing that
   * leaves this component.
   */
  const [iso, setIso] = useState(() => countryOf(value).iso);

  /** The last string this component emitted, so it can tell its own echo apart. */
  const emitted = useRef<string | null>(null);

  // A value arriving from anywhere else — an edit form loading a driver, a
  // customer picked from the search list — brings its own country with it.
  // Our own echo is ignored, or choosing Canada would immediately be
  // overwritten by the United States on the next render.
  useEffect(() => {
    if (value === emitted.current) return;
    if (value !== '') setIso(countryOf(value).iso);
  }, [value]);

  const chosen =
    COUNTRIES.find((option) => option.iso === iso) ??
    COUNTRIES.find((option) => option.iso === HOME_COUNTRY)!;

  const digits = value.replace(/\D/g, '');

  // If the number plainly is not from the chosen country, trust the number for
  // this render. That is the gap between an outside change landing and the
  // effect above adopting it — one frame, but a frame in which an edit form
  // would otherwise show +977 beside a full Indian number.
  const country =
    digits === '' || digits.startsWith(chosen.dial) ? chosen : countryOf(value);

  const subscriber = digits.startsWith(country.dial)
    ? digits.slice(country.dial.length)
    : digits;

  // An empty number is empty, not a bare country code: a `+977` sitting in the
  // database is a value that looks like a phone number and is not one, and
  // every "did they leave this blank" check downstream would be fooled by it.
  // Safe to keep now that the country no longer depends on what is emitted.
  const join = (dial: string, rest: string): void => {
    const next = rest === '' ? '' : `+${dial}${rest}`;
    emitted.current = next;
    onChange(next);
  };

  return (
    <div className="flex gap-2">
      <div className="w-28 shrink-0">
        <CountrySelect
          value={country.iso}
          onChange={(picked) => {
            setIso(picked);
            const chosen = COUNTRIES.find((option) => option.iso === picked);
            if (chosen !== undefined) join(chosen.dial, subscriber);
          }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          aria-describedby={describedBy}
          value={subscriber}
          onChange={(event) => join(country.dial, event.target.value.replace(/\D/g, ''))}
          className={`${CONTROL} tnum`}
        />
      </div>
    </div>
  );
}

/** `YYYY-MM-DD` → `21/09/2026`. */
function dayFirst(iso: string): string {
  if (iso === '') return '';
  const [year = '', month = '', day = ''] = iso.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * A typed date, read day first, to `YYYY-MM-DD` — or null if it is not one.
 *
 * Accepts the separators people actually type (/ - . or a space) and one- or
 * two-digit days and months, because "1/9/2026" is how a date gets typed at
 * speed. The year must be four digits: "26" could be 1926 or 2026, and a
 * booking a century out is not a typo anybody should have to find.
 *
 * Round-tripped through a real Date so 31/02 is refused rather than quietly
 * becoming the 3rd of March.
 */
function parseDayFirst(text: string): string | null {
  const match = /^\s*(\d{1,2})[/\-. ](\d{1,2})[/\-. ](\d{4})\s*$/.exec(text);
  if (match === null) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

interface DateInputProps {
  readonly id: string;
  readonly describedBy: string | undefined;
  /** `YYYY-MM-DD`, or empty. */
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * A date, day first, whatever language the browser is set to.
 *
 * This was the browser's own `<input type="date">`, and that control takes its
 * format from the browser's language — not from the page. On a machine set to
 * English (US) it shows 09/21/2026, month first, and nothing the page does can
 * change that. The client asked for day, month, year, and on their own laptops
 * the native box was the one place that could not give it to them.
 *
 * So the date is typed as text — dd/mm/yyyy — and the browser's calendar is
 * still one press away on the button beside it. The native picker is kept
 * because a calendar grid has no format to get wrong; only its text box did.
 */
export function DateInput({ id, describedBy, value, onChange }: DateInputProps): ReactElement {
  const [text, setText] = useState(() => dayFirst(value));
  const picker = useRef<HTMLInputElement>(null);

  /** What this component last sent, so its own echo is not mistaken for news. */
  const emitted = useRef<string | null>(null);

  // A value from elsewhere — an edit form loading, the calendar button —
  // replaces what is shown. Our own echo does not, or every keystroke would be
  // reformatted under the cursor.
  useEffect(() => {
    if (value === emitted.current) return;
    setText(dayFirst(value));
  }, [value]);

  const send = (next: string): void => {
    emitted.current = next;
    onChange(next);
  };

  // Something typed that is not a date. Shown, not swallowed: the box keeps
  // what was typed and turns red, rather than snapping back to the last good
  // date and leaving somebody to save a booking for a day they did not mean.
  const unreadable = text.trim() !== '' && parseDayFirst(text) === null;

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="dd/mm/yyyy"
        aria-describedby={describedBy}
        aria-invalid={unreadable}
        title={unreadable ? 'Not a date. Type it as day/month/year, like 21/09/2026.' : undefined}
        value={text}
        onChange={(event) => {
          const typed = event.target.value;
          setText(typed);
          if (typed.trim() === '') {
            send('');
            return;
          }
          // Nothing is sent while the date is half typed. "21/0" is not a
          // date yet, and sending it would make the form ask the server which
          // cars are free on no day at all.
          const read = parseDayFirst(typed);
          if (read !== null) send(read);
        }}
        onBlur={() => {
          // Tidied to one shape once the cursor leaves — "1-9-2026" becomes
          // 01/09/2026 — but only when it was a real date.
          if (!unreadable) setText(dayFirst(value));
        }}
        className={`${CONTROL} tnum pr-10 ${unreadable ? 'border-alert' : ''}`}
      />

      <button
        type="button"
        aria-label="Pick from a calendar"
        onClick={() => {
          try {
            picker.current?.showPicker();
          } catch {
            // A browser without showPicker, or one refusing it: the typed box
            // still works, so put the cursor there rather than doing nothing.
            document.getElementById(id)?.focus();
          }
        }}
        className="absolute right-1 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded text-ink-muted hover:bg-paper hover:text-ink"
      >
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          aria-hidden="true"
        >
          <rect x="3.5" y="5" width="17" height="15" rx="1.5" />
          <path d="M3.5 9.5h17M8 3v4M16 3v4" />
        </svg>
      </button>

      {/* The browser's calendar, borrowed for its grid. Invisible rather than
          `display: none`, because a picker can only open on something the
          browser is actually rendering. */}
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value}
        onChange={(event) => {
          setText(dayFirst(event.target.value));
          send(event.target.value);
        }}
        className="pointer-events-none absolute bottom-0 right-0 h-px w-px opacity-0"
      />
    </div>
  );
}

interface DateTimeInputProps {
  readonly id: string;
  readonly describedBy: string | undefined;
  /** `YYYY-MM-DDTHH:mm`, the same value a datetime-local input carries. */
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/**
 * A date box and a time box, side by side.
 *
 * This was one `datetime-local` input. The native widget crams a date, a
 * clock and a stepper into one control, renders differently in every browser,
 * and was called ugly by the people using it every day — fairly.
 *
 * Two plain inputs instead. The value crossing the boundary is unchanged, so
 * nothing above this has to know: it is still one `YYYY-MM-DDTHH:mm` string.
 * Splitting it also keeps the time, which matters more than it looks — a
 * booking that goes out in the morning and comes back at night is the case
 * the client asked for by name, and a date-only field would quietly make
 * every booking a whole day again.
 */
export function DateTimeInput({
  id,
  describedBy,
  value,
  onChange,
}: DateTimeInputProps): ReactElement {
  const [date = '', time = ''] = value.split('T');

  // A half-filled value is not a moment. Holding the other half until both
  // are present stops the form reading a bare date as midnight and quietly
  // saving a booking nobody asked for.
  const join = (nextDate: string, nextTime: string): void => {
    onChange(nextDate === '' ? '' : `${nextDate}T${nextTime === '' ? '00:00' : nextTime}`);
  };

  // Width lives on the wrappers, not on the inputs. `CONTROL` already carries
  // `w-full`, and putting `w-28` beside it left two width utilities of equal
  // specificity on one element — which wins is down to the order Tailwind
  // happens to emit them in, not the order they are written. `w-full` won,
  // the time box grew to fill the row, and the page gained a horizontal
  // scrollbar. A wrapper that owns the width cannot be argued with.
  return (
    <div className="flex gap-2">
      <div className="min-w-0 flex-1">
        <DateInput
          id={id}
          describedBy={describedBy}
          value={date}
          onChange={(nextDate) => join(nextDate, time)}
        />
      </div>
      <div className="w-[7.5rem] shrink-0">
        <input
          type="time"
          value={time}
          aria-label="Time"
          onChange={(event) => join(date, event.target.value)}
          className={`${CONTROL} tnum`}
        />
      </div>
    </div>
  );
}

interface NumberInputProps {
  readonly id: string;
  readonly describedBy: string | undefined;
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  readonly min?: number;
  /** Shown inside the field's right edge: "km", "seats", "%". */
  readonly unit?: string;
}

export function NumberInput({
  id,
  describedBy,
  value,
  onChange,
  min = 0,
  unit,
}: NumberInputProps): ReactElement {
  return (
    <div className="relative">
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        value={value === null ? '' : String(value)}
        aria-describedby={describedBy}
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
        className={`${CONTROL} tnum ${unit !== undefined ? 'pr-12' : ''}`}
      />
      {unit !== undefined && (
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-small text-ink-muted">
          {unit}
        </span>
      )}
    </div>
  );
}

interface MoneyInputProps {
  readonly id: string;
  readonly describedBy: string | undefined;
  readonly value: Money;
  readonly onChange: (value: Money) => void;
}

/**
 * Money is typed in rupees and stored in paisa.
 *
 * Staff type "8500", not "850000", and certainly not "8500.00". The conversion
 * happens here, at the boundary, once — the rest of the app only ever sees
 * integer paisa.
 */
export function MoneyInput({ id, describedBy, value, onChange }: MoneyInputProps): ReactElement {
  const { locale } = useAppConfig();

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-small text-ink-muted">
        {locale.currencyPrefix}
      </span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        aria-describedby={describedBy}
        value={value === 0 ? '' : formatMoneyDigits(value, locale.numberGrouping)}
        onChange={(event) => {
          const parsed = parseRupeeInput(event.target.value);
          onChange(parsed ?? paisa(0));
        }}
        className={`${CONTROL} tnum pl-9 text-right`}
      />
    </div>
  );
}

interface SelectInputProps<T extends string> {
  readonly id: string;
  readonly describedBy: string | undefined;
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly options: readonly { readonly value: T; readonly label: string }[];
  /**
   * For the few selects that stand on their own without a `Field` above them.
   *
   * Everything else here is labelled by a real `<label>`; this exists for the
   * calendar's range picker, which sits in a row of buttons where a label
   * would push it out of line. Without it that control has no name at all to
   * anyone not looking at the screen.
   */
  readonly ariaLabel?: string;
  /**
   * Height, in the same vocabulary as `Button`.
   *
   * `touch` — 44px — is the default and what every form uses: these are typed
   * into at speed by somebody with a customer waiting. `base` is 32px and
   * exists for a select that sits in a row of buttons, where 44px makes it
   * stand a dozen pixels taller than everything beside it.
   */
  readonly size?: 'base' | 'touch';
}

export function SelectInput<T extends string>({
  id,
  describedBy,
  value,
  onChange,
  options,
  ariaLabel,
  size = 'touch',
}: SelectInputProps<T>): ReactElement {
  return (
    <select
      id={id}
      value={value}
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      onChange={(event) => onChange(event.target.value as T)}
      className={size === 'touch' ? CONTROL : CONTROL_COMPACT}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

interface CheckboxProps {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}

/** No hint here either, for the reason given on `Field`. */
export function Checkbox({ label, checked, onChange }: CheckboxProps): ReactElement {
  const id = useId();
  return (
    <div className="flex items-start gap-2.5 py-1">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded-sm border-rule-strong text-signal"
      />
      <div>
        <label htmlFor={id} className="text-body">
          {label}
        </label>
      </div>
    </div>
  );
}

/** A row of two or three fields, stacking on a phone. */
export function FieldRow({ children }: { children: ReactNode }): ReactElement {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}
